import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyMFA, isAuthenticated } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [isSendingForgot, setIsSendingForgot] = useState(false);

  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    const saved = localStorage.getItem("client_remember_email");
    if (saved) {
      setEmail(saved);
      setRemember(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setNeedsVerification(false);
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
        setIsLoading(false);
        return;
      }
      if (remember) localStorage.setItem("client_remember_email", email);
      else localStorage.removeItem("client_remember_email");
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      if (err?.requiresVerification) {
        setNeedsVerification(true);
        setError(err.message || "Tu cuenta aún no está verificada.");
      } else {
        setError(err.message || "Credenciales inválidas");
      }
      setIsLoading(false);
    }
  };

  const handleGoToVerification = () => {
    navigate("/register", { state: { email } });
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken) return;
    setError("");
    setIsLoading(true);
    try {
      await verifyMFA(mfaToken, mfaCode);
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setError(err.message || "Código MFA inválido");
      setIsLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError("");
    setForgotMessage("");
    setIsSendingForgot(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || "https://worker-auth.betalog-ventas.workers.dev"}/auth/client/forgot-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error al enviar enlace");
      setForgotMessage(data.message || "Revisa tu correo para restablecer la contraseña");
    } catch (err: any) {
      setForgotError(err.message);
    } finally {
      setIsSendingForgot(false);
    }
  };

  if (mfaToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-10">
            <div className="text-center mb-6">
              <img src="/img/logo.png" alt="Gran Arquitectura" className="h-14 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900">Verificación de Dos Factores</h1>
              <p className="text-sm text-slate-500 mt-1">Ingresa el código de 6 dígitos</p>
            </div>
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                autoFocus
                className="w-full text-center text-2xl tracking-[0.5em] py-3 border-2 border-slate-200 rounded-lg focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading || mfaCode.length !== 6}
                className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg font-semibold disabled:opacity-50 transition"
              >
                {isLoading ? "Verificando..." : "Verificar Código"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMfaToken(null);
                  setMfaCode("");
                  setError("");
                }}
                className="w-full text-sm text-slate-500 hover:text-primary"
              >
                ← Volver al inicio de sesión
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-10">
          <div className="text-center mb-8">
            <img src="/img/logo.png" alt="Gran Arquitectura" className="h-14 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900">Gran Arquitectura</h1>
            <p className="text-sm text-slate-500 mt-1">Accede a tu portal de cliente</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                disabled={isLoading}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none transition"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Contraseña
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none transition"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 accent-primary"
                />
                Recordarme
              </label>
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="text-sm text-primary hover:underline font-medium"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                <p>{error}</p>
                {needsVerification && (
                  <button
                    type="button"
                    onClick={handleGoToVerification}
                    className="mt-2 text-primary hover:underline font-semibold text-sm"
                  >
                    → Ir a verificar mi correo
                  </button>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg font-semibold disabled:opacity-50 transition flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                "Iniciar Sesión"
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500">
              ¿No tienes cuenta?{" "}
              <Link to="/register" className="text-primary hover:underline font-semibold">
                Regístrate aquí
              </Link>
            </p>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100 text-center">
            <Link to="/" className="text-xs text-slate-400 hover:text-primary">
              ← Volver al inicio
            </Link>
          </div>
        </div>
      </div>

      {showForgot && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setShowForgot(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowForgot(false)}
              className="absolute top-4 right-5 text-slate-400 hover:text-slate-700 text-xl"
            >
              ×
            </button>
            <div className="text-center mb-5">
              <div className="text-4xl mb-2">🔑</div>
              <h2 className="text-xl font-bold text-slate-900">Recuperar Contraseña</h2>
              <p className="text-sm text-slate-500 mt-1">
                Te enviaremos un enlace para restablecer tu contraseña
              </p>
            </div>

            <form onSubmit={handleForgot} className="space-y-4">
              <input
                type="email"
                required
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="tu@correo.com"
                autoFocus
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />

              {forgotError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                  {forgotError}
                </div>
              )}
              {forgotMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-3">
                  {forgotMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isSendingForgot}
                className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg font-semibold disabled:opacity-50 transition"
              >
                {isSendingForgot ? "Enviando..." : "Enviar enlace"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}